import { baseApi } from '../base-api.js';

type MonthlyEvaluationCount = { count: number; month: string; year: number };
type MonthlyEvaluationCountResponse = { monthlyCount: MonthlyEvaluationCount };

/**
 * Mirrors `apps/server/src/modules/evaluation` (`evaluationController`, prefix `/evaluations`) —
 * a single route today: the monthly evaluation-session quota that drives the paywall. No shared
 * type existed for its response shape (`evaluation.service.ts`'s `getMonthlyEvaluationCount`
 * returns `{ count, month, year }` inline), so it's declared locally here rather than invented in
 * `packages/shared/src/types`.
 *
 * `evaluation-session.api.ts`'s `createEvaluationSession`/`deleteEvaluationSession` invalidate
 * this endpoint's tag: the count is a live `COUNT(*) WHERE started_at >= <start of month>` query
 * (see `evaluation.repository.ts`), not a stored counter, so both creating and deleting a session
 * this month change what it returns. `completeEvaluationSession`/archive/unarchive do not, since
 * they never change the row count, only fields on an existing row.
 */
export const evaluationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMonthlyEvaluationCount: builder.query<MonthlyEvaluationCount, void>({
      query: () => ({ path: ['evaluations', 'monthly-count'], method: 'get' }),
      transformResponse: (response: MonthlyEvaluationCountResponse) => response.monthlyCount,
      providesTags: [{ type: 'Evaluation', id: 'MONTHLY_COUNT' }],
    }),
  }),
});

export const { useGetMonthlyEvaluationCountQuery } = evaluationApi;
